'use client';

import React, { useEffect, useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import Image from 'next/image';
import { FooterLogo } from '../media/footerLogo';
import { FaPhone, FaEnvelope, FaMapMarkerAlt } from 'react-icons/fa';
import { useForm, ValidationError } from '@formspree/react';
import { NavbarLogo } from '../media/navbarLogo';


export default function Footer() {
  const { currentLanguage, isRTL, texts } = useLanguage();
  const [footerText, setFooterText] = useState(null);
  const [loading, setLoading] = useState(true);
  const [state, handleSubmit] = useForm("xnnpobeg");

  useEffect(() => {
    const fetchFooterText = async () => {
      try {
        const res = await fetch('/api/footer');
        if (!res.ok) {
          throw new Error('Failed to fetch footer text');
        }
        const data = await res.json();
        setFooterText(data.footerText);
      } catch (error) {
        console.error('Error fetching footer text:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchFooterText();
  }, []);

  // Get footer text content with fallback to context
  const getContent = (path) => {
    if (!footerText || !path) return null;
    
    const parts = path.split('.');
    let value = footerText;
    
    for (const part of parts) {
      if (!value || !value[part]) return null;
      value = value[part];
    }
    
    return value[currentLanguage];
  };

  const getButtonStyle = () => {
    if (state.succeeded) {
      return 'bg-green-800 text-white';
    }
    if (state.errors?.length) {
      return 'bg-red-500 text-white';
    }
    return 'bg-white text-gray-700 hover:bg-opacity-90';
  };

  const getButtonText = () => {
    if (state.submitting) return texts.footer.contact.sending;
    if (state.succeeded) return texts.footer.contact.success || 'Message sent successfully!';
    if (state.errors?.length) return texts.footer.contact.error || 'Failed to send';
    return texts.footer.contact.button;
  };

  return (
    <div className="">
      <footer id="contact-section" className="bg-gradient-to-b from-blue-300 to-[#3066B4] w-full">
        <div className="w-full px-[4%] sm:px-[5%] py-[5vh]">
          {/* Top section with logo, tagline, navigation and contact form */}
          <div className={`grid grid-cols-1 md:grid-cols-2 gap-[2rem] mb-[3rem] ${isRTL ? 'text-right md:[direction:rtl]' : ''}`}>
            <div>
              {/* Logo and tagline */}
              <div className="mb-[2rem]">
                <h2 className="text-[1.875rem] font-bold">
                  <span className="inline-block">
                    <FooterLogo />
                  </span>
                </h2>
                <p className="mt-[1rem] font-bold text-white max-w-md">
                  {getContent('footer.title') || texts.footer.company.description}
                </p>
              </div>
              
              {/* Sitemap and Companies sections */}
              <div className={`grid grid-cols-2 gap-[2rem] ${isRTL ? 'flex-row-reverse' : ''}`}>
                <div>
                  <h4 className="font-extrabold mb-[1rem] text-white">
                    {getContent('sitemap.title') || texts.footer.sitemap.title}
                  </h4>
                  <ul className="space-y-[0.5rem]">
                    <li>
                      <a href="#vision-section" className="hover:underline text-white transition-all duration-300">
                        {getContent('sitemap.about') || texts.header.navigation.about}
                      </a>
                    </li>
                    <li>
                      <a href="#projects-section" className="hover:underline text-white transition-all duration-300">
                        {getContent('sitemap.projects') || texts.header.navigation.projects}
                      </a>
                    </li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-extrabold mb-[1rem] text-white">
                    {getContent('companies.title') || texts.footer.companies.title}
                  </h4>
                  <ul className="space-y-[0.5rem]">
                    <li>
                      <a href="#" className="hover:underline text-white transition-all duration-300">
                        {getContent('companies.paradise') || texts.companies.paradise.title}
                      </a>
                    </li>
                    <li>
                      <a href="#" className="hover:underline text-white transition-all duration-300">
                        {getContent('companies.farma') || texts.companies.farma.title}
                      </a>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
            
            {/* Contact form section */}
            <div>
              <h3 className="text-[1.25rem] font-extrabold mb-[1.5rem] text-white">{texts.footer.contact.title}</h3>
              <form onSubmit={handleSubmit} className="space-y-[1rem]">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-[1rem]">
                  <div className="border-b border-white pb-[0.25rem] transition-all duration-300 hover:border-opacity-80">
                    <input 
                      type="text" 
                      name="name"
                      placeholder={texts.footer.contact.placeholders.name} 
                      className={`p-[0.5rem] w-full bg-transparent text-white placeholder-white placeholder-opacity-70 focus:outline-none transition-all duration-300 ${isRTL ? 'text-right' : ''}`}
                      required
                    />
                    <ValidationError prefix="Name" field="name" errors={state.errors} />
                  </div>
                  <div className="border-b border-white pb-[0.25rem] transition-all duration-300 hover:border-opacity-80">
                    <input 
                      type="text" 
                      name="surname"
                      placeholder={texts.footer.contact.placeholders.surname} 
                      className={`p-[0.5rem] w-full bg-transparent text-white placeholder-white placeholder-opacity-70 focus:outline-none transition-all duration-300 ${isRTL ? 'text-right' : ''}`}
                      required
                    />
                    <ValidationError prefix="Surname" field="surname" errors={state.errors} />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-[1rem]">
                  <div className="border-b border-white pb-[0.25rem] transition-all duration-300 hover:border-opacity-80">
                    <input 
                      type="text" 
                      name="phone"
                      placeholder={texts.footer.contact.placeholders.phone} 
                      className={`p-[0.5rem] w-full bg-transparent text-white placeholder-white placeholder-opacity-70 focus:outline-none transition-all duration-300 ${isRTL ? 'text-right' : ''}`}
                      required
                    />
                    <ValidationError prefix="Phone" field="phone" errors={state.errors} />
                  </div>
                  <div className="border-b border-white pb-[0.25rem] transition-all duration-300 hover:border-opacity-80">
                    <input 
                      type="email" 
                      name="email"
                      placeholder={texts.footer.contact.placeholders.email} 
                      className={`p-[0.5rem] w-full bg-transparent text-white placeholder-white placeholder-opacity-70 focus:outline-none transition-all duration-300 ${isRTL ? 'text-right' : ''}`}
                      required
                    />
                    <ValidationError prefix="Email" field="email" errors={state.errors} />
                  </div>
                </div>
                <div className="bg-white rounded-md transition-all duration-300 hover:shadow-lg">
                  <textarea 
                    name="message"
                    placeholder={texts.footer.contact.placeholders.message} 
                    className={`p-[0.75rem] rounded-md bg-white text-gray-700 w-full focus:outline-none transition-all duration-300 ${isRTL ? 'text-right' : ''}`}
                    rows="3"
                    required
                  ></textarea>
                  <ValidationError prefix="Message" field="message" errors={state.errors} />
                </div>
                
                <div className={isRTL ? 'text-right' : 'text-right'}>
                  <button
                    type="submit"
                    disabled={state.submitting || state.succeeded}
                    className={`px-[1.5rem] py-[0.75rem] rounded-md font-medium text-[0.875rem]
                      transition-all duration-300 ease-in-out hover:transform hover:scale-105
                      ${state.submitting || state.succeeded ? 'cursor-not-allowed' : ''}
                      ${getButtonStyle()}`}
                  >
                    {getButtonText()}
                  </button>
                </div>
              </form>
            </div>
          </div>
          
          {/* Bottom copyright and contact info */}
          <div className={`pt-[2rem] border-t border-white/30 flex flex-col md:flex-row ${isRTL ? 'justify-between flex-row-reverse' : 'justify-between'} items-center`}>
            <div>
              <p className="text-[0.875rem] text-white">{texts.footer.copyright}</p>
            </div>
            {/* Contact info - stack on mobile, row on desktop */}
            <div className={`flex flex-col md:flex-row items-center ${isRTL ? 'space-x-reverse md:space-x-[2rem] flex-row-reverse' : 'md:space-x-[2rem]'} mt-[1rem] md:mt-0 space-y-[1rem] md:space-y-0`}>
              <div className={`flex items-center ${isRTL ? 'space-x-reverse space-x-[0.5rem] flex-row-reverse' : 'space-x-[0.5rem]'}`}>
                <span className="inline-block">
                  <svg width="16" height="16" viewBox="0 0 24 24" className="fill-white">
                    <path d="M12 0C7.453 0 3.623 3.853 3.623 8.429c0 6.502 7.18 14.931 7.48 15.283a1.176 1.176 0 001.795 0c.299-.352 7.479-8.781 7.479-15.283C20.377 3.853 16.547 0 12 0zm0 12.714c-2.368 0-4.286-1.918-4.286-4.285C7.714 6.076 9.632 4.143 12 4.143s4.286 1.933 4.286 4.286c0 2.367-1.918 4.285-4.286 4.285z"/>
                  </svg>
                </span>
                <span className="text-white">{texts.footer.info.location}</span>
              </div>
              <div className={`flex items-center ${isRTL ? 'space-x-reverse space-x-[0.5rem] flex-row-reverse' : 'space-x-[0.5rem]'}`}>
                <span className="inline-block">
                  <svg width="16" height="16" viewBox="0 0 24 24" className="fill-white">
                    <path d="M1.5 8.67v8.58a3 3 0 003 3h15a3 3 0 003-3V8.67l-8.928 5.493a3 3 0 01-3.144 0L1.5 8.67z"/>
                    <path d="M22.5 6.908V6.75a3 3 0 00-3-3h-15a3 3 0 00-3 3v.158l9.714 5.978a1.5 1.5 0 001.572 0L22.5 6.908z"/>
                  </svg>
                </span>
                <span className="text-white">{texts.footer.info.email}</span>
              </div>
              <div className={`flex items-center ${isRTL ? 'space-x-reverse space-x-[0.5rem] flex-row-reverse' : 'space-x-[0.5rem]'}`}>
                <span className="inline-block">
                  <svg width="16" height="16" viewBox="0 0 24 24" className="fill-white">
                    <path d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z"/>
                  </svg>
                </span>
                <span className="text-white">{texts.footer.info.phone}</span>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}